import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, getPostfixWithRobotId, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_SES, URL_SES } from "../../constants/api_endpoints";
import {
	IAddStructureProps,
	IAddStructureResponse,
	IAddUserdataProps,
	IAddUserdataResponse,
	IDeleteStructureProps,
	IDeleteUserdataProps,
	IGetUserdataListProps,
	IModifyStructureProps,
	IModifyUserdataProps,
	IReplaceUserdataProps,
	ISesUserdataStructureState,
	IStructureResponse,
	IUserdataItem,
} from "../../types/ses/sesUserdataStructureTypes";
import { IResponse, RequestStatus, ResponseStatus } from "../../types/statusTypes";

const initialState: ISesUserdataStructureState = {
	structuresList: {
		data: null,
		activeStructure: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	addingStructure: {
		id: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	modifyStructure: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	deletingStructure: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},

	userdataList: {
		data: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	addingUserdata: {
		id: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	modifyUserdata: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	replaceUserdata: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	deletingUserdata: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// список структур пользовательских данных
export const getStructuresList = createAsyncThunk("sesUserdata/structureList", async (robotId: string, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IStructureResponse[] | IResponse> = await axiosApi.get(
			`${URL_SES}/${ENDPOINTS_SES.USERDATA_STRUCTURE_LIST}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// добавление структуры пользовательских данных
export const addStructure = createAsyncThunk("sesUserdata/addStructure", async ({ robotId, data }: IAddStructureProps, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IAddStructureResponse> = await axiosApi.post(
			`${URL_SES}/${ENDPOINTS_SES.USERDATA_STRUCTURE_ADD}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}`,
			{
				data: JSON.stringify(data),
			}
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// изменение структуры пользовательских данных
export const modifyStructure = createAsyncThunk(
	"sesUserdata/editStructure",
	async ({ robotId, structureId, data }: IModifyStructureProps, { getState, rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponse> = await axiosApi.post(
				`${URL_SES}/${ENDPOINTS_SES.USERDATA_STRUCTURE_MODIFY}/${getPostfixWithRobotId(
					(getState() as RootState).sesRobot.robotList.activeRobotVersion,
					robotId
				)}/${structureId}`,
				{
					data: JSON.stringify(data),
				}
			);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// удаление структуры пользовательских данных
export const deleteStructure = createAsyncThunk("sesUserdata/deleteStructure", async ({ robotId, structureId }: IDeleteStructureProps, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(
			`${URL_SES}/${ENDPOINTS_SES.USERDATA_STRUCTURE_DELETE}/${getPostfixWithRobotId(
				(getState() as RootState).sesRobot.robotList.activeRobotVersion,
				robotId
			)}/${structureId}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// список пользовательских данных
export const getUserdataList = createAsyncThunk("sesUserdata/list", async ({ robotId, structureId }: IGetUserdataListProps, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IUserdataItem[] | IResponse> = await axiosApi.get(
			`${URL_SES}/${ENDPOINTS_SES.USERDATA_DATA_LIST}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}/${structureId}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// добавление пользовательских данных (не используется)
export const addUserdata = createAsyncThunk("sesUserdata/add", async ({ robotId, structureId, data }: IAddUserdataProps, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IAddUserdataResponse> = await axiosApi.post(
			`${URL_SES}/${ENDPOINTS_SES.USERDATA_DATA_ADD}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}/${structureId}`,
			{
				data: JSON.stringify(data),
			}
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// изменение пользовательских данных (не используется)
export const modifyUserdata = createAsyncThunk(
	"sesUserdata/edit",
	async ({ robotId, structureId, dataId, data }: IModifyUserdataProps, { getState, rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponse> = await axiosApi.post(
				`${URL_SES}/${ENDPOINTS_SES.USERDATA_DATA_MODIFY}/${getPostfixWithRobotId(
					(getState() as RootState).sesRobot.robotList.activeRobotVersion,
					robotId
				)}/${structureId}/${dataId}`,
				{
					data: JSON.stringify(data),
				}
			);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// замена пользовательских данных
export const replaceUserdata = createAsyncThunk(
	"sesUserdata/replace",
	async ({ robotId, structureId, replace, data }: IReplaceUserdataProps, { getState, rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponse> = await axiosApi.post(
				`${URL_SES}/${ENDPOINTS_SES.USERDATA_DATA_PUT}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}/${structureId}`,
				{
					replace,
					list: JSON.stringify(data),
				}
			);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// удаление пользовательских данных (не используется)
export const deleteUserdata = createAsyncThunk("sesUserdata/delete", async ({ robotId, structureId, dataId }: IDeleteUserdataProps, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(
			`${URL_SES}/${ENDPOINTS_SES.USERDATA_DATA_DELETE}/${getPostfixWithRobotId(
				(getState() as RootState).sesRobot.robotList.activeRobotVersion,
				robotId
			)}/${structureId}/${dataId}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const sesUserdataStructureSlice = createSlice({
	name: "sesUserdata",
	initialState,
	reducers: {
		// добавление активной структуры
		addActiveStructure: (state, action: PayloadAction<IStructureResponse>) => {
			state.structuresList.activeStructure = action.payload;
		},
		// добавление записи справочника
		addDirectoryEntry: (state, action: PayloadAction<IUserdataItem>) => {
			if (Array.isArray(state.userdataList.data)) {
				state.userdataList.data.push(action.payload);
			}
		},
		// изменение записи справочника
		changeDirectoryEntry: (state, action: PayloadAction<{ index: number; field: string; value: string }>) => {
			if (Array.isArray(state.userdataList.data)) {
				state.userdataList.data[action.payload.index][action.payload.field] = action.payload.value;
			}
		},
		// удаление записей из справочника
		deleteEntriesFromDirectory: (state, action: PayloadAction<{ rows: number[] }>) => {
			if (Array.isArray(state.userdataList.data)) {
				state.userdataList.data = state.userdataList.data.filter((_row, idx) => !action.payload.rows.includes(idx));
			}
		},
		// очистка state
		clearState: (state) => {
			state.structuresList = initialState.structuresList;
			state.addingStructure = initialState.addingStructure;
			state.modifyStructure = initialState.modifyStructure;
			state.deletingStructure = initialState.deletingStructure;
			state.userdataList = initialState.userdataList;
			state.addingUserdata = initialState.addingUserdata;
			state.modifyUserdata = initialState.modifyUserdata;
			state.replaceUserdata = initialState.replaceUserdata;
			state.deletingUserdata = initialState.deletingUserdata;
		},
		// очистка списка структур
		clearStructuresList: (state) => {
			state.structuresList = initialState.structuresList;
		},
		// очистка списка структур кроме активного
		clearStructuresListExceptActive: (state) => {
			state.structuresList.data = initialState.structuresList.data;
			state.structuresList.error = initialState.structuresList.error;
			state.structuresList.message = initialState.structuresList.message;
			state.structuresList.status = initialState.structuresList.status;
		},
		// очистка статуса добавления структуры
		clearAddingStructure: (state) => {
			state.addingStructure = initialState.addingStructure;
		},
		// очистка статуса изменения структуры
		clearModifyStructure: (state) => {
			state.modifyStructure = initialState.modifyStructure;
		},
		// очистка статуса удаления структуры
		clearDeletingStructure: (state) => {
			state.deletingStructure = initialState.deletingStructure;
		},
		// очистка списка данных структуры
		clearUserdataList: (state) => {
			state.userdataList = initialState.userdataList;
		},
		// очистка статуса добавления данных структуры
		clearAddingUserdata: (state) => {
			state.addingUserdata = initialState.addingUserdata;
		},
		// очистка статуса изменения данных структуры
		clearModifyUserdata: (state) => {
			state.modifyUserdata = initialState.modifyUserdata;
		},
		// очистка статуса замены данных структуры
		clearReplaceUserdata: (state) => {
			state.replaceUserdata = initialState.replaceUserdata;
		},
		// очистка статуса удаления данных структуры
		clearDeletingUserdata: (state) => {
			state.deletingUserdata = initialState.deletingUserdata;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getStructuresList.pending, (state) => {
				state.structuresList.status = RequestStatus.LOADING;
			})
			.addCase(getStructuresList.fulfilled, (state, action) => {
				if (Array.isArray(action.payload)) {
					state.structuresList.status = RequestStatus.IDLE;
					state.structuresList.data = action.payload;
				} else state.structuresList.status = RequestStatus.FAILED;
			})
			.addCase(getStructuresList.rejected, (state, action: PayloadAction<unknown>) => {
				state.structuresList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.structuresList.error = error;
				state.structuresList.message = message;
			})
			.addCase(addStructure.pending, (state) => {
				state.addingStructure.status = RequestStatus.LOADING;
			})
			.addCase(addStructure.fulfilled, (state, action) => {
				if (action.payload) {
					state.addingStructure.status = RequestStatus.IDLE;
					state.addingStructure.error = action.payload.error;
					state.addingStructure.message = action.payload.message;
					if ("id" in action.payload && action.payload.id) state.addingStructure.id = action.payload.id;
				} else state.addingStructure.status = RequestStatus.FAILED;
			})
			.addCase(addStructure.rejected, (state, action: PayloadAction<unknown>) => {
				state.addingStructure.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.addingStructure.error = error;
				state.addingStructure.message = message;
			})
			.addCase(modifyStructure.pending, (state) => {
				state.modifyStructure.status = RequestStatus.LOADING;
			})
			.addCase(modifyStructure.fulfilled, (state, action) => {
				if (action.payload) {
					state.modifyStructure.status = RequestStatus.IDLE;
					state.modifyStructure.error = action.payload.error;
					state.modifyStructure.message = action.payload.message;
				} else state.modifyStructure.status = RequestStatus.FAILED;
			})
			.addCase(modifyStructure.rejected, (state, action: PayloadAction<unknown>) => {
				state.modifyStructure.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.modifyStructure.error = error;
				state.modifyStructure.message = message;
			})
			.addCase(deleteStructure.pending, (state) => {
				state.deletingStructure.status = RequestStatus.LOADING;
			})
			.addCase(deleteStructure.fulfilled, (state, action) => {
				if (action.payload) {
					state.deletingStructure.status = RequestStatus.IDLE;
					state.deletingStructure.error = action.payload.error;
					state.deletingStructure.message = action.payload.message;
				} else state.deletingStructure.status = RequestStatus.FAILED;
			})
			.addCase(deleteStructure.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingStructure.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingStructure.error = error;
				state.deletingStructure.message = message;
			})
			.addCase(getUserdataList.pending, (state) => {
				state.userdataList.status = RequestStatus.LOADING;
			})
			.addCase(getUserdataList.fulfilled, (state, action) => {
				if (Array.isArray(action.payload)) {
					state.userdataList.status = RequestStatus.IDLE;
					state.userdataList.data = action.payload;
				} else state.userdataList.status = RequestStatus.FAILED;
			})
			.addCase(getUserdataList.rejected, (state, action: PayloadAction<unknown>) => {
				state.userdataList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.userdataList.error = error;
				state.userdataList.message = message;
			})
			.addCase(addUserdata.pending, (state) => {
				state.addingUserdata.status = RequestStatus.LOADING;
			})
			.addCase(addUserdata.fulfilled, (state, action) => {
				if (action.payload) {
					state.addingUserdata.status = RequestStatus.IDLE;
					state.addingUserdata.error = action.payload.error;
					state.addingUserdata.message = action.payload.message;
					if ("id" in action.payload && action.payload.id) state.addingUserdata.id = action.payload.id;
				} else state.addingUserdata.status = RequestStatus.FAILED;
			})
			.addCase(addUserdata.rejected, (state, action: PayloadAction<unknown>) => {
				state.addingUserdata.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.addingUserdata.error = error;
				state.addingUserdata.message = message;
			})
			.addCase(modifyUserdata.pending, (state) => {
				state.modifyUserdata.status = RequestStatus.LOADING;
			})
			.addCase(modifyUserdata.fulfilled, (state, action) => {
				if (action.payload) {
					state.modifyUserdata.status = RequestStatus.IDLE;
					state.modifyUserdata.error = action.payload.error;
					state.modifyUserdata.message = action.payload.message;
				} else state.modifyUserdata.status = RequestStatus.FAILED;
			})
			.addCase(modifyUserdata.rejected, (state, action: PayloadAction<unknown>) => {
				state.modifyUserdata.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.modifyUserdata.error = error;
				state.modifyUserdata.message = message;
			})
			.addCase(replaceUserdata.pending, (state) => {
				state.replaceUserdata.status = RequestStatus.LOADING;
			})
			.addCase(replaceUserdata.fulfilled, (state, action) => {
				if (action.payload) {
					state.replaceUserdata.status = RequestStatus.IDLE;
					state.replaceUserdata.error = action.payload.error;
					state.replaceUserdata.message = action.payload.message;
				} else state.replaceUserdata.status = RequestStatus.FAILED;
			})
			.addCase(replaceUserdata.rejected, (state, action: PayloadAction<unknown>) => {
				state.replaceUserdata.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.replaceUserdata.error = error;
				state.replaceUserdata.message = message;
			})
			.addCase(deleteUserdata.pending, (state) => {
				state.deletingUserdata.status = RequestStatus.LOADING;
			})
			.addCase(deleteUserdata.fulfilled, (state, action) => {
				if (action.payload) {
					state.deletingUserdata.status = RequestStatus.IDLE;
					state.deletingUserdata.error = action.payload.error;
					state.deletingUserdata.message = action.payload.message;
				} else state.deletingUserdata.status = RequestStatus.FAILED;
			})
			.addCase(deleteUserdata.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingUserdata.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingUserdata.error = error;
				state.deletingUserdata.message = message;
			});
	},
});

export const {
	addActiveStructure,
	addDirectoryEntry,
	changeDirectoryEntry,
	deleteEntriesFromDirectory,
	clearState,
	clearStructuresList,
	clearStructuresListExceptActive,
	clearAddingStructure,
	clearModifyStructure,
	clearDeletingStructure,
	clearUserdataList,
	clearAddingUserdata,
	clearModifyUserdata,
	clearReplaceUserdata,
	clearDeletingUserdata,
} = sesUserdataStructureSlice.actions;

export const selectStructuresList = (state: RootState) => state.sesUserdataStructure.structuresList;
export const selectAddingStructure = (state: RootState) => state.sesUserdataStructure.addingStructure;
export const selectModifyStructure = (state: RootState) => state.sesUserdataStructure.modifyStructure;
export const selectDeletingStructure = (state: RootState) => state.sesUserdataStructure.deletingStructure;

export const selectUserdataList = (state: RootState) => state.sesUserdataStructure.userdataList;
export const selectAddingUserdata = (state: RootState) => state.sesUserdataStructure.addingUserdata;
export const selectModifyUserdata = (state: RootState) => state.sesUserdataStructure.modifyUserdata;
export const selectReplaceUserdata = (state: RootState) => state.sesUserdataStructure.replaceUserdata;
export const selectDeletingUserdata = (state: RootState) => state.sesUserdataStructure.deletingUserdata;

export default sesUserdataStructureSlice.reducer;
